import {
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Collapse,
	FormControl,
	IconButton,
	IconButtonProps,
	InputLabel,
	MenuItem,
	Select,
	styled,
	TextField,
} from '@mui/material'
import * as React from 'react'
import { memo } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { DatePicker } from '@mui/x-date-pickers'
import { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import { ChangeOrderFilters, statusOptions } from '../../types/Work/ChangeOrder'
import { useChangeOrderContext } from './ChangeOrderContext'
dayjs.extend(timezone)

interface ExpandMoreProps extends IconButtonProps {
	expand: boolean
}
const ExpandMore = styled((props: ExpandMoreProps) => {
	const { expand, ...other } = props
	return <IconButton {...other} />
})(({ theme, expand }: { theme: any; expand: boolean }) => ({
	marginLeft: 'auto',
	transition: theme.transitions.create('transform', {
		duration: theme.transitions.duration.shortest,
	}),
	transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
}))

function FilterHeaderComponent() {
	const [expanded, setExpanded] = React.useState(true)
	const { filters, setFilters, assignees } = useChangeOrderContext()
	const { projectTimeZone } = useProjectFeaturesContext()
	const handleExpandClick = () => {
		setExpanded(!expanded)
	}

	const handleFilterChange = (field: string, value: any) => {
		setFilters((prev: ChangeOrderFilters) => ({
			...prev,
			[field]: value,
			page: 0,
		}))
	}

	const handleDateChange = (field: string, date: Dayjs | null) => {
		if (date) {
			const utcDate = date.tz(projectTimeZone || 'UTC').utc()
			handleFilterChange(field, utcDate)
		} else {
			handleFilterChange(field, null)
		}
	}

	function handleClearFilters(): void {
		setFilters({
			status: null,
			reportedById: null,
			reportedOn: null,
			searchWorkItem: null,
			page: 0,
			surveyId: null
		})
	}

	return (
		<Card sx={{ maxHeight: '20%', mt: 1 }} elevation={2}>
			<CardHeader
				sx={{ py: 0, cursor: 'pointer' }}
				subheader={
					<>
						Filters
						<ExpandMore
							expand={expanded}
							onClick={handleExpandClick}
							aria-expanded={expanded}
							aria-label='show more'
						>
							<ExpandMoreIcon />
						</ExpandMore>
					</>
				}
				onClick={handleExpandClick}
			/>
			<CardContent sx={{ py: 0, '&:last-child': { pb: 2 } }}>
				<Collapse in={expanded} timeout={200} sx={{ pb: 1 }}>
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: 2,
							width: '90%',
							p: 0,
						}}
					>
						<Box sx={{ flex: '0 1 5%' }}>
							<TextField
								label='ID'
								variant='filled'
								fullWidth
								size='small'
								type='number'
								value={filters.surveyId ?? ""}
								onChange={(e) => handleFilterChange('surveyId', e.target.value)}
							/>
						</Box>
						<Box sx={{ flex: '0 1 17%' }}>
							<TextField
								label='Work Item Name'
								variant='filled'
								fullWidth
								size='small'
								value={filters.searchWorkItem ?? ""}
								onChange={(e) => handleFilterChange('searchWorkItem', e.target.value)}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<Autocomplete
								disablePortal
								options={assignees}
								key={filters.reportedById ?? 'empty'}
								value={
									filters.reportedById != null
									  ? assignees.find((x) => x.id === filters.reportedById) ?? null
									  : null
								  }
								getOptionLabel={(option) => option.fullName}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								onChange={(_, newValue) =>
									handleFilterChange('reportedById', newValue ? newValue.id : null)
								}
								renderInput={(params) => (
									<TextField {...params} label='Reported By' variant='filled' size='small' />
								)}
							/>
						</Box>
						
						<Box sx={{ flex: '0 1 12%' }}>
							<DatePicker
								label='Reported On'
								value={filters.reportedOn}
								onChange={(date: Dayjs | null) => handleDateChange('reportedOn', date)}
								renderInput={(props) => <TextField {...props} variant='filled' size='small' />}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<FormControl variant='filled' fullWidth>
								<InputLabel id='status-label'>Status</InputLabel>
								<Select
									labelId='status-label'
									value={filters.status}
									onChange={(e) => handleFilterChange('status', e.target.value)}
									size='small'
								>
									{Object.keys(statusOptions).map((x, index) => (
										<MenuItem value={x} key={index}>
											{statusOptions[x]}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
						<Box sx={{ flex: '0 1 7%', display: 'flex' }}>
							<Button
								onClick={handleClearFilters}
								variant='text'
								sx={{
									textTransform: 'none',
									color: 'gray',
									textDecoration: 'underline',
									'&:hover': {
										textDecoration: 'none',
										color: 'darkblue',
									},
								}}
							>
								Clear Filters
							</Button>
						</Box>
					</Box>
				</Collapse>
			</CardContent>
		</Card>
	)
}

// Memoize the FilterHeader component to prevent unnecessary re-renders
const FilterHeader = memo(FilterHeaderComponent);

export default FilterHeader;
